import { useCallback, useMemo, useState } from 'react';

export const CONSULTED_APPLICATIONS_KEY_PREFIX = 'consultedApplications';
export const MAX_CONSULTED_MISSIONS = 300;

// Per admin, so two admins sharing a browser each see their own reading.
export const consultedApplicationsStorageKey = (adminId) =>
  `${CONSULTED_APPLICATIONS_KEY_PREFIX}:${adminId || 'shared'}`;

// Storage can be missing, full, blocked or hold anything: every read falls
// back to an empty record and every write is best effort.
const readRecord = (storageKey) => {
  try {
    const record = JSON.parse(window.localStorage.getItem(storageKey));
    return record && typeof record === 'object' && !Array.isArray(record)
      ? record
      : {};
  } catch (error) {
    return {};
  }
};

const writeRecord = (storageKey, record) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(record));
  } catch (error) {
    // Nothing to do: the list simply renders as never consulted.
  }
};

/**
 * The candidates whose temp-worker sheet the admin has opened from a mission's
 * lists, in this browser: `{ [missionId]: [candidateUserId, …] }` under a key
 * per admin.
 *
 * The record keeps the most recently marked missions last and drops the
 * oldest beyond MAX_CONSULTED_MISSIONS; this relies on mission ids not being
 * integer-like (ObjectIds), since those keys would be sorted first.
 */
const useConsultedApplications = (adminId, missionId) => {
  const storageKey = consultedApplicationsStorageKey(adminId);
  const missionKey = missionId ? String(missionId) : '';
  // Bumped on each mark: a dependency of the read, so the ids are read again.
  const [version, setVersion] = useState(0);

  const consultedIds = useMemo(() => {
    if (!missionKey) return new Set();
    const ids = readRecord(storageKey)[missionKey];
    return new Set(Array.isArray(ids) ? ids.map(String) : []);
  }, [storageKey, missionKey, version]);

  const isConsulted = useCallback(
    (candidateId) =>
      candidateId != null && consultedIds.has(String(candidateId)),
    [consultedIds],
  );

  const markConsulted = useCallback(
    (candidateId) => {
      if (!missionKey || candidateId == null) return;
      const id = String(candidateId);
      const record = readRecord(storageKey);
      const ids = Array.isArray(record[missionKey]) ? record[missionKey] : [];
      // Deleted then set again, so this mission becomes the most recent.
      delete record[missionKey];
      record[missionKey] = ids.includes(id) ? ids : [...ids, id];
      const missionKeys = Object.keys(record);
      missionKeys
        .slice(0, Math.max(0, missionKeys.length - MAX_CONSULTED_MISSIONS))
        .forEach((key) => delete record[key]);
      writeRecord(storageKey, record);
      setVersion((current) => current + 1);
    },
    [storageKey, missionKey],
  );

  return { isConsulted, markConsulted };
};

export default useConsultedApplications;
